import React from "react";
import { Box, Button, Link } from "@amzn/awsui-components-react";
import {
  Investigation,
  RelatedInvestigation,
  Stage,
} from "../../lusca-search-api/generated-src";
import { relationsFromIds } from "./InvestigationRelated";
import { investigationPath } from "./investigationUtils";
import { isListPopulation } from "./statusHint";

const MAX_CHILDREN = 3;

/**
 * One line each for the case this one was split from and the cases split off it, on the Details
 * card. Titles come from `related` (ids only on older deployments); the full list stays on the
 * Related tab.
 */
export const InvestigationFamilyLinks: React.FC<{
  investigation: Investigation;
  stage: Stage;
  /** InvestigationDetail.related (titles); falls back to the ids on the case. */
  related?: RelatedInvestigation[] | null;
  onViewRelated?: () => void;
}> = ({ investigation, stage, related: given, onViewRelated }) => {
  const related = given?.length ? given : relationsFromIds(investigation);
  const parent = related.find((r) => r.relation === "parent");
  const children = related.filter((r) => r.relation === "child");
  if (!parent && children.length === 0) return null;
  const link = (id: string, title?: string) => (
    <Link href={investigationPath(id, stage)} fontSize="body-s">
      {title || id}
    </Link>
  );
  return (
    <Box
      fontSize="body-s"
      color="text-body-secondary"
      data-testid="family-links"
    >
      {parent && (
        <div>
          {isListPopulation(investigation)
            ? "Split by list from "
            : "Split from "}
          {link(parent.investigationId, parent.title)}
        </div>
      )}
      {children.length > 0 && (
        <div>
          Split into{" "}
          {children.slice(0, MAX_CHILDREN).map((c, i) => (
            <React.Fragment key={c.investigationId}>
              {i > 0 && ", "}
              {link(c.investigationId, c.title)}
            </React.Fragment>
          ))}
          {children.length > MAX_CHILDREN && (
            <>
              {" and "}
              {onViewRelated ? (
                <Button variant="inline-link" onClick={onViewRelated}>
                  {children.length - MAX_CHILDREN} more
                </Button>
              ) : (
                `${children.length - MAX_CHILDREN} more`
              )}
            </>
          )}
        </div>
      )}
    </Box>
  );
};
